import { useState } from "react";
import { ChevronDown, ChevronUp, Download, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { L, money, RateBadge, TaxBadge } from "./common";
import { t, fmtDateTime } from "@/i18n";

const BankDetails = ({ p }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm" data-testid="bank-details">
    <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Nombre legal")}</p><p className="font-medium">{p.legal_name}</p></div>
    <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Identificación fiscal")}</p><p className="font-medium">{p.tax_id || "—"}</p></div>
    <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("Dirección")}</p><p className="font-medium">{p.address || "—"}</p></div>
    {p.bank_fields.map((f) => (
      <div key={f.key}><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{L(f)}</p><p className="font-mono text-sm">{p.bank?.[f.key] || "—"}</p></div>
    ))}
  </div>
);

export const ProfilesTable = ({ items, onChanged }) => {
  const [open, setOpen] = useState(null);
  const review = async (p, status) => {
    const note = status === "rejected" ? window.prompt(t("Motivo del rechazo (se mostrará al artista):")) ?? "" : "";
    if (status === "rejected" && note === null) return;
    try { await api.put(`/payments/profiles/${p.user.id}/tax-form`, { status, note }); toast.success(t("Formulario actualizado")); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel overflow-hidden">
      <Table data-testid="profiles-table">
        <TableHeader><TableRow><TableHead>{t("Cuenta")}</TableHead><TableHead>{t("País")}</TableHead><TableHead>{t("Retención")}</TableHead><TableHead>{t("Formulario")}</TableHead><TableHead className="text-right">{t("Por pagar")}</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {items.map((row) => {
            const p = row.profile;
            const isOpen = open === row.user.id;
            return [
              <TableRow key={row.user.id} data-testid={`profile-row-${row.user.id}`}>
                <TableCell><p className="font-medium">{row.user.artist_name || row.user.name}</p><p className="text-xs text-muted-foreground">{row.user.email} · {row.user.role}</p></TableCell>
                <TableCell>{p ? L(p.country_name) : <span className="text-xs text-muted-foreground">{t("Sin datos de pago")}</span>}</TableCell>
                <TableCell>{p ? <RateBadge rate={p.treaty_rate} treaty={p.treaty} /> : "—"}</TableCell>
                <TableCell>
                  {p ? <div className="flex items-center gap-2 flex-wrap"><span className="text-xs">{p.tax_form_required}</span><TaxBadge status={p.tax_form_status} testId={`profile-tax-status-${row.user.id}`} />
                    {p.tax_form_file_id && <a href={fileUrl(p.tax_form_file_id)} target="_blank" rel="noreferrer" className="text-primary" data-testid={`profile-tax-file-${row.user.id}`}><Download className="h-3.5 w-3.5" /></a>}</div> : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">{row.pending.count ? <span className="font-semibold">{money(row.pending.net)}</span> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {p && p.tax_form_status === "submitted" && <>
                    <Button size="sm" variant="outline" className="h-7 gap-1 mr-1" onClick={() => review(row, "approved")} data-testid={`profile-approve-${row.user.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> {t("Aprobar")}</Button>
                    <Button size="sm" variant="ghost" className="h-7 gap-1 mr-1 text-rose-300" onClick={() => review(row, "rejected")} data-testid={`profile-reject-${row.user.id}`}><XCircle className="h-3.5 w-3.5" /> {t("Rechazar")}</Button>
                  </>}
                  {p && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setOpen(isOpen ? null : row.user.id)} data-testid={`profile-toggle-${row.user.id}`}>{isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</Button>}
                </TableCell>
              </TableRow>,
              isOpen && p && <TableRow key={`${row.user.id}-d`} className="bg-muted/20 hover:bg-muted/20"><TableCell colSpan={6} className="p-4"><BankDetails p={p} /><p className="text-[11px] text-muted-foreground mt-3">{t("Actualizado")} {fmtDateTime(p.updated_at)}</p></TableCell></TableRow>,
            ];
          })}
        </TableBody>
      </Table>
    </div>
  );
};
